import {sendEvent, type SendEventContext} from '@github-ui/hydro-analytics'
import {hpcAttrs} from '@github-ui/hpc'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner, useMergedRefs} from '@primer/react'
import type {CSSProperties, ReactNode, Ref} from 'react'
import {Profiler, useEffect, useMemo, useRef, useState} from 'react'
import styles from './IpmBannerBase.module.css'

export interface IpmBannerBaseProps {
  dismissPath: string
  visibleEvent: string
  dismissEvent: string
  testId: string
  title: string
  icon?: ReactNode
  description: ReactNode
  primaryAction?: ReactNode
  variant?: 'critical' | 'info' | 'success' | 'upsell' | 'warning'
  /** Hides the banner's leading visual, including the variant's default icon. */
  hideLeadingVisual?: boolean
  /** Applied to the banner's wrapper, for consumers restyling the Primer surface. */
  className?: string
  style?: CSSProperties
  rounded: boolean
  location: string
  telemetryTags?: IpmMetricTags
  ref?: Ref<HTMLDivElement>
}

export function IpmBannerBase({
  dismissPath,
  visibleEvent,
  dismissEvent,
  testId,
  title,
  icon,
  description,
  primaryAction,
  variant,
  hideLeadingVisual,
  className,
  style,
  rounded,
  location,
  telemetryTags: telemetryTagsProp,
  ref,
}: IpmBannerBaseProps) {
  const [hidden, setHidden] = useState<boolean>(false)
  const bannerRef = useRef<HTMLDivElement>(null)
  const mergedRef = useMergedRefs(ref ?? null, bannerRef)
  const defaultTelemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'IpmBannerBase',
      surface: 'banner',
      display_location: location,
      variant: variant ?? 'default',
    }),
    [location, variant],
  )
  const telemetryTags = telemetryTagsProp ?? defaultTelemetryTags
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    if (hidden || !telemetryTagsProp) return

    const element = bannerRef.current
    if (!element) return

    let hasReportedVisible = false
    const reportVisibleWhenDisplayed = () => {
      if (hasReportedVisible || element.getClientRects().length === 0) return

      hasReportedVisible = true
      reportIpmVisible(telemetryTags)
    }

    reportVisibleWhenDisplayed()
    if (hasReportedVisible || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(reportVisibleWhenDisplayed)
    observer.observe(element)
    return () => observer.disconnect()
  }, [hidden, telemetryTags, telemetryTagsProp])

  const handleDismiss = async () => {
    sendEvent('analytics.click', JSON.parse(dismissEvent) as SendEventContext)

    if (telemetryTagsProp) {
      reportIpmDismiss(telemetryTags)
    }

    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      if (telemetryTagsProp) {
        reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      }
      // Silently handle network errors to maintain a good UX
    } finally {
      setHidden(true)
    }
  }

  if (hidden) {
    return null
  }

  const commonBannerProps = {
    title,
    hideTitle: true,
    'data-testid': `${testId}-inner`,
    className: rounded ? undefined : styles.squareBanner,
    description,
    primaryAction,
    onDismiss: handleDismiss,
  }

  // Custom visual is only supported for the info and upsell variants
  const variantAndVisualProps =
    !variant || variant === 'info' || variant === 'upsell' ? {variant, leadingVisual: icon} : {variant}

  const banner = (
    <div
      ref={mergedRef}
      tabIndex={-1}
      {...hpcAttrs()}
      style={style}
      className={
        [hideLeadingVisual ? styles.hiddenLeadingVisual : undefined, className].filter(Boolean).join(' ') || undefined
      }
      data-testid={testId}
      data-analytics-visible={visibleEvent}
    >
      <Banner {...commonBannerProps} {...variantAndVisualProps} />
    </div>
  )

  const content = !renderTiming.isEnabled ? (
    banner
  ) : (
    <Profiler id="IpmBannerBase" onRender={renderTiming.onRender}>
      {banner}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="IpmBannerBase">
      {content}
    </IpmErrorBoundary>
  )
}
